import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  SparklesIcon,
  RocketLaunchIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/solid";

const Home = () => {
  interface NewsItem {
    id: number;
    title: string;
    content: string;
    date: string;
    game: string;
  }

  const [news, setNews] = useState<NewsItem[]>([]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/news.json`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => setNews(data.newsList))
      .catch((error) => console.error("Error fetching news:", error));
  }, []);

  const truncateContent = (content: string) => {
    return content.length > 80 ? content.substring(0, 80) + "..." : content;
  };

  const featureCards = [
    {
      title: "빠른 시작",
      description: "즉시 접근 가능한 프로젝트와 빠른 피드백 루프를 제공합니다.",
      icon: RocketLaunchIcon,
      color: "from-sky-500 to-indigo-500",
    },
    {
      title: "신뢰성",
      description: "최적화된 서버 구조로 안정적인 서비스를 지원합니다.",
      icon: ShieldCheckIcon,
      color: "from-emerald-500 to-teal-500",
    },
    {
      title: "커뮤니티",
      description: "함께 성장하는 경험을 위한 최신 소식을 제공합니다.",
      icon: ChatBubbleLeftRightIcon,
      color: "from-fuchsia-500 to-pink-500",
    },
  ];

  return (
    <div className="pb-24 bg-[#fafafa] dark:bg-[#050505] transition-colors duration-500">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.15),_transparent_25%),radial-gradient(circle_at_bottom_right,_rgba(236,72,153,0.12),_transparent_18%)]"></div>
        <div className="relative px-6 pt-16 pb-24 mx-auto max-w-7xl sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-white/70 text-sm font-semibold text-slate-800 shadow-lg shadow-slate-900/5 backdrop-blur-lg dark:bg-slate-900/70 dark:text-slate-100 dark:shadow-black/10">
                <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Now building the future
              </div>
              <div>
                <h1 className="text-5xl font-black tracking-tight text-slate-950 sm:text-6xl lg:text-7xl dark:text-white">
                  MinDevX와 함께하는 새로운 디지털 여정
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
                  기술과 감성을 잇는 인터랙티브한 경험. 창의적인 프로젝트와 강력한 서비스가 한곳에 모였습니다.
                </p>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/nyangnyang"
                  className="inline-flex items-center justify-center rounded-3xl bg-gradient-to-r from-indigo-600 to-violet-500 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-indigo-500/20 transition hover:from-indigo-500 hover:to-purple-500"
                >
                  프로젝트 탐색
                  <ArrowRightIcon className="w-5 h-5 ml-3" />
                </Link>
                <Link
                  to="/ping"
                  className="inline-flex items-center justify-center rounded-3xl border border-slate-200 bg-white/90 px-8 py-4 text-base font-semibold text-slate-900 transition hover:border-indigo-500 hover:bg-white dark:border-slate-700 dark:bg-slate-950/80 dark:text-white dark:hover:bg-slate-900"
                >
                  서버 상태 확인
                </Link>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-[2rem] bg-white/90 p-8 shadow-2xl shadow-slate-900/5 ring-1 ring-slate-200/80 backdrop-blur-xl dark:bg-slate-950/80 dark:ring-slate-800">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 dark:text-indigo-400">
                  오늘의 하이라이트
                </p>
                <h2 className="mt-4 text-3xl font-bold text-slate-950 dark:text-white">
                  이제, 더욱 선명해진 경험
                </h2>
                <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  프로젝트, 뉴스, 서버 상태를 한 곳에서 빠르게 확인하고 바로 이동할 수 있습니다.
                </p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {featureCards.map((feature) => (
                    <div key={feature.title} className="rounded-3xl border border-slate-200/80 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/80">
                      <div className={`inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br ${feature.color} text-white shadow-lg shadow-slate-900/10`}> 
                        <feature.icon className="w-5 h-5" />
                      </div>
                      <h3 className="mt-4 text-base font-semibold text-slate-950 dark:text-white">
                        {feature.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-[2rem] border border-slate-200/80 bg-white/80 p-8 shadow-2xl shadow-slate-900/5 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/80">
                <h3 className="text-xl font-bold text-slate-950 dark:text-white">빠른 접근</h3>
                <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  모든 주요 콘텐츠가 단일 플랫폼에 정리되어 있습니다. 뉴스, 미니게임, 버그 리포트까지 빠르게 이동하세요.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <Link to="/bugs" className="rounded-3xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800">
                    버그 제보
                  </Link>
                  <Link to="/minigames" className="rounded-3xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 transition hover:border-indigo-500 dark:border-slate-700 dark:text-white">
                    미니게임
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 mb-12 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-600 dark:text-indigo-400">
              Featured Works
            </p>
            <h2 className="mt-3 text-4xl font-bold text-slate-950 dark:text-white sm:text-5xl">
              최신 프로젝트를 만나보세요.
            </h2>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
            <SparklesIcon className="w-5 h-5 text-indigo-500" />
            매주 새로운 업데이트가 추가됩니다.
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-12">
          <div className="relative md:col-span-8 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl shadow-slate-900/5 transition hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-950">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-pink-500/10"></div>
            <div className="relative z-10 flex h-full flex-col justify-between p-10 md:p-14">
              <div>
                <div className="inline-flex items-center justify-center mb-8 h-20 w-20 rounded-[2rem] bg-white text-4xl shadow-2xl dark:bg-slate-900">
                  😸
                </div>
                <h3 className="mb-4 text-4xl font-bold text-slate-950 dark:text-white">
                  냥냥식당타이쿤
                </h3>
                <p className="max-w-lg text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                  귀여운 고양이들과 함께하는 힐링 경영 시뮬레이션. 당신만의 감성으로 최고의 식당을 만들어보세요.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 mt-12">
                <div className="flex flex-wrap gap-3">
                  {['Simulation', 'Tycoon', 'Cute'].map((tag) => (
                    <span key={tag} className="rounded-2xl bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:bg-white/5 dark:text-slate-300">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link to="/nyangnyang" className="inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-indigo-600 text-white transition hover:bg-indigo-500">
                  <ArrowRightIcon className="w-6 h-6" />
                </Link>
              </div>
            </div>
          </div>

          <div className="space-y-6 md:col-span-4">
            <div className="rounded-[2.5rem] bg-slate-950 p-10 text-white shadow-2xl shadow-slate-950/20 transition hover:-translate-y-1">
              <div className="inline-flex items-center justify-center mb-6 h-14 w-14 rounded-3xl bg-gradient-to-br from-emerald-400 to-teal-400 text-xl shadow-xl">
                ⚔️
              </div>
              <h3 className="mb-3 text-2xl font-bold">LiteStar</h3>
              <p className="text-sm leading-7 text-slate-300">
                Liter를 사용하여 싸움을 하고 스토리를 발견해내자!
              </p>
              <Link to="/litestar" className="mt-8 inline-flex w-full items-center justify-center rounded-3xl bg-white px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-100">
                Play Now
              </Link>
            </div>

            <div className="rounded-[2.5rem] border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-600 dark:text-indigo-400">
                Quick Access
              </p>
              <h3 className="mt-4 text-2xl font-bold text-slate-950 dark:text-white">
                바로 이동하기
              </h3>
              <div className="mt-6 grid gap-3">
                <Link to="/bugs" className="rounded-3xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 transition hover:border-indigo-500 dark:border-slate-700 dark:text-white">
                  버그 제보
                </Link>
                <Link to="/minigames" className="rounded-3xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 transition hover:border-indigo-500 dark:border-slate-700 dark:text-white">
                  미니게임
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 mb-12 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-600 dark:text-indigo-400">
              뉴스 업데이트
            </p>
            <h2 className="mt-3 text-4xl font-bold text-slate-950 dark:text-white sm:text-5xl">
              최신 소식을 한눈에.
            </h2>
          </div>
          <Link to="/news" className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
            VIEW ALL →
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {news.slice(0, 3).map((item) => (
            <Link
              to={`/news?id=${item.id}`}
              key={item.id}
              className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 shadow-lg transition hover:-translate-y-1 hover:border-indigo-300 hover:shadow-2xl dark:border-slate-800 dark:bg-slate-950"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                  {item.game}
                </span>
                <span className="font-mono text-sm text-slate-400 dark:text-slate-400">
                  {item.date}
                </span>
              </div>
              <h3 className="mb-4 text-xl font-bold text-slate-950 transition-colors group-hover:text-indigo-600 dark:text-white">
                {item.title}
              </h3>
              <p className="leading-7 text-slate-600 dark:text-slate-300 line-clamp-2">
                {truncateContent(item.content)}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section className="px-6 pb-20 mx-auto max-w-7xl">
        <div className="grid gap-4 md:grid-cols-4">
          {[
            {
              label: "Games",
              val: "2+",
              icon: RocketLaunchIcon,
              color: "from-sky-500 to-indigo-500",
            },
            {
              label: "Uptime",
              val: "99.9%",
              icon: GlobeAltIcon,
              color: "from-emerald-500 to-teal-500",
            },
            {
              label: "Updates",
              val: "Weekly",
              icon: SparklesIcon,
              color: "from-violet-500 to-fuchsia-500",
            },
            {
              label: "Community",
              val: "Active",
              icon: ChatBubbleLeftRightIcon,
              color: "from-pink-500 to-rose-500",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-[2rem] border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-900/5 transition hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-950"
            >
              <div className={`mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-gradient-to-br ${stat.color} text-white shadow-lg`}> 
                <stat.icon className="w-6 h-6" />
              </div>
              <div className="text-3xl font-black text-slate-950 dark:text-white">
                {stat.val}
              </div>
              <div className="mt-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-500">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
